import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { CpdCategoryStatus, cpdCategoryTypeWithLabel } from '../../../../constants';
import { adminActions } from '../../../../state/modules/admin';
import { contentConfigSelector } from '../../../../state/modules/configuration/selectors';
import { industriesSelector } from '../../../../state/modules/industry/selectors';
import { noCapMap, noCapRemove } from '../../../../utils';
import { ControlGroup, ControlOption, FileUploadFormField, Icon, ImageCropField, InputFormField, ServerError, TextAreaFormField } from '../../../components';
import Loading from '../../loading';

const iconLibrary = 'https://thinkcaddie.sharepoint.com/CadCorp/Documents/Forms/AllItems.aspx?FolderCTID=0x012000E9CBDFC5EE54074EB136B8BEB563FBDC&id=%2FCadCorp%2FDocuments%2FDESIGN%2FDefault%20CPD%20Category%20Icons';

const validate = ({ name }, industries) => ({
  name: (name && name.length > 0) ? null : 'Name is required.',
  isUnique: fp.size(fp.uniqBy(ind => ind.industryId)(industries)) === fp.size(industries) ? null : 'Assigned industries must be unique.'
})

const isValid = ({ name, isUnique }) => !name && !isUnique

const AdminCpdCategoryEdit = ({
  cpdCategory,
  icon,
  error,
  updateName,
  updateDescriptions,
  removeIcon,
  updateCpdCategory,
  updateLegacy,
  updateCategoryType,
  onIconDrop,
  onImageCropped,
  croppedIconUrl,
  maxImageFileSizeBytes,
  validation,
  showingCroppingModal,
  hideCroppingModal
}) => (
    <div id='section--admin-cpd-category-edit'>
      <h1 className='text-center'>
        {
          cpdCategory.id ? 'Edit CPD Category' : 'Add CPD Category'
        }
      </h1>
      <Grid>
        <Row className='md-gutter'>
          <Col xs={12} md={8} mdOffset={2} className='boxed text-left'>
            <form onSubmit={updateCpdCategory}>
              <Row>
                <Col xs={12}>
                  <ServerError error={error} hideSupport />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <InputFormField {...{
                    id: 'name',
                    onChange: (e) => updateName(e.target.value),
                    validation: validation.name,
                    value: cpdCategory.name
                  }} />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <TextAreaFormField {...{
                    id: 'descriptions',
                    onChange: (e) => updateDescriptions(e.target.value),
                    value: cpdCategory.descriptions
                  }} />
                </Col>
              </Row>
              <ControlGroup
                control='checkbox'
                type='styled'
                label='Legacy'
                onChange={(e) => updateLegacy(!cpdCategory.legacy)}>
                <ControlOption checked={cpdCategory.legacy} label={<span>Legacy</span>} />
              </ControlGroup>
              <Row>
                <Col xs={12}>
                  <ControlGroup
                    label='CPD Category Type'
                    control='radio'
                    type='styled'
                    onChange={(e) => updateCategoryType(e.target.value)}
                    colProps={{ xs: 12, sm: 4 }}>
                    {
                      fp.map(({value, label}) =>
                        <ControlOption
                          key={value}
                          value={value}
                          label={<span>{fp.startCase(label)}</span>}
                          checked={cpdCategory.categoryType === value}
                        />
                      )(cpdCategoryTypeWithLabel)
                    }
                  </ControlGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <h3>Upload Icon</h3>
                  <p className='icon-link'>
                    Click <a href={iconLibrary} target='_blank'>here</a> to view the default caddie icons
                  </p>
                </Col>
                <Col xs={12}>
                  {
                    cpdCategory.iconUrl && !icon ?
                      [
                        <div key='icon' className='business-logo existing'><img alt="business logo" src={he.decode(cpdCategory.iconUrl)} /></div>,
                        <button key='remove-icon' aria-label='Remove Icon' className='btn remove-business-logo' onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeIcon()
                        }}>
                          <Icon icon='close' />
                        </button>
                      ] : null
                  }
                  {
                    croppedIconUrl ?
                      [
                        <div key='icon' className='business-logo new'><img alt="business logo" src={croppedIconUrl} /></div>,
                        <button key='remove-icon' aria-label='Remove Icon' className='btn remove-business-logo' onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeIcon()
                        }}>
                          <Icon icon='close' />
                        </button>
                      ] : null
                  }
                  {
                    (!cpdCategory.iconUrl && !icon) ? <FileUploadFormField
                      styleName='file-6'
                      multiple={false}
                      accept='image/png,image/jpeg'
                      onFileDrop={onIconDrop}
                      file={cpdCategory.icon}
                      maxSize={maxImageFileSizeBytes}
                      dropLabel='Upload Icon (JPG, PNG)' /> : null
                  }
                </Col>
              </Row>
              <Row className='top-gap'>
                <Col xs={12}>
                  <Button
                    className='flex-center'
                    type='submit'>
                    {
                      cpdCategory.id ? 'Update' : 'Create'
                    }
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Grid>
      <Modal show={showingCroppingModal} onHide={hideCroppingModal} dialogClassName='image-cropping-modal'>
        <Modal.Body>
          <ImageCropField
            className='business-logo new'
            imageSrc={icon ? icon.preview : null}
            onComplete={onImageCropped}
            width={96}
            height={96} />
          <button aria-label='Remove Icon' className='btn remove-business-logo under-crop-field' onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            removeIcon()
          }}>
            <Icon icon='close' />
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );

const defaultIndustryCpdCategory = {
  industryId: 1,
  minimumCpd: 0,
  mandatory: false
}

const mapStateToProps = (state) => ({
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  industryCpdCategory: state.admin.industryCpdCategory,
  industries: industriesSelector(state),
  error: state.admin.error,
  config: contentConfigSelector(state)
})
const mapDispatchToProps = (dispatch) => ({
  updateCpdCategory: bindActionCreators(adminActions.admin.updateCpdCategory, dispatch),
  addCpdCategory: bindActionCreators(adminActions.admin.addCpdCategory, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ config }) => ({
    maxImageFileSizeBytes: config.maxImageFileSizeBytes,
  })),
  branch(
    ({ cpdCategorys, industryCpdCategory }) => cpdCategorys.length === 0 || industryCpdCategory.length === 0,
    renderComponent(Loading)
  ),
  withStateHandlers(
    ({
      cpdCategorys,
      industryCpdCategory,
      match: {
        params: { cpdCategoryId }
      }
    }) => ({
      cpdCategory: cpdCategoryId ?
        cpdCategorys.find(({ id }) => id === parseInt(cpdCategoryId, 10)) : {
          name: undefined,
          descriptions: '',
          legacy: false,
          status: CpdCategoryStatus.active,
          publisherVisible: false,
          categoryType: fp.first(cpdCategoryTypeWithLabel).value
        },
      mappedIndustryCpdCategory: cpdCategoryId ?
        fp.filter(({ cpdCategoryId: cid }) => cid === parseInt(cpdCategoryId, 10))(industryCpdCategory)
        : [],
      icon: undefined,
      croppedIconUrl: undefined,
      showingCroppingModal: false,
      validation: {}
    }),
    {
      updateName: ({ cpdCategory }) => (name) => ({
        cpdCategory: {
          ...cpdCategory,
          name
        }
      }),
      updateDescriptions: ({ cpdCategory }) => (descriptions) => ({
        cpdCategory: {
          ...cpdCategory,
          descriptions
        }
      }),
      updateLegacy: ({ cpdCategory }) => (legacy) => ({
        cpdCategory: {
          ...cpdCategory,
          legacy
        }
      }),
      updateCategoryType: ({ cpdCategory }) => (categoryType) => ({
        cpdCategory: {
          ...cpdCategory,
          categoryType
        }
      }),
      setIndustry: ({ mappedIndustryCpdCategory }) => (index, industryId) => ({
        mappedIndustryCpdCategory: noCapMap(
          (industryCpdCategory, idx) => index === idx ? {
            ...industryCpdCategory,
            industryId: parseInt(industryId, 10),
            minimumCpd: 0,
            mandatory: false
          } : industryCpdCategory
        )(mappedIndustryCpdCategory)
      }),
      setParentCpdCategory: ({ mappedIndustryCpdCategory }) => (index, parentId) => ({
        mappedIndustryCpdCategory: noCapMap(
          (industryCpdCategory, idx) => index === idx ? {
            ...industryCpdCategory,
            parentCpdCategoryId: parentId ? parseInt(parentId, 10) : undefined,
            minimumCpd: 0,
            mandatory: false
          } : industryCpdCategory
        )(mappedIndustryCpdCategory)
      }),
      removeIndustry: ({ mappedIndustryCpdCategory }) => (index) => ({
        mappedIndustryCpdCategory: noCapRemove((_, i) => i === index)(mappedIndustryCpdCategory)
      }),
      addIndustry: ({ mappedIndustryCpdCategory }) => () => ({
        mappedIndustryCpdCategory: mappedIndustryCpdCategory.concat([defaultIndustryCpdCategory])
      }),
      updateIcon: () => (icon) => ({
        icon
      }),
      removeIcon: ({ cpdCategory }) => () => ({
        icon: undefined,
        croppedIconUrl: undefined,
        cpdCategory: {
          ...cpdCategory,
          iconUrl: undefined,
          iconFileName: undefined
        },
        showingCroppingModal: false
      }),
      onImageCropped: () => (blob) => ({
        icon: blob,
        showingCroppingModal: false,
        croppedIconUrl: window.URL.createObjectURL(blob)
      }),
      showCroppingModal: () => () => ({
        showingCroppingModal: true
      }),
      hideCroppingModal: () => () => ({
        showingCroppingModal: false
      }),
      setValidation: () => (validation) => ({ validation })
    }
  ),
  withHandlers({
    onIconDrop: ({ updateIcon, showCroppingModal }) => (acceptedFiles, rejectedFiles) => {
      const acceptedFile = fp.first(acceptedFiles);
      if (acceptedFile) {
        updateIcon(acceptedFile);
        showCroppingModal();
      }
    }
  }),
  withHandlers({
    updateCpdCategory: ({ updateCpdCategory, addCpdCategory, cpdCategory, icon, setValidation, mappedIndustryCpdCategory }) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      const validation = validate(cpdCategory, mappedIndustryCpdCategory)
      if (isValid(validation)) {
        if (cpdCategory.id) {
          updateCpdCategory(
            cpdCategory.id,
            {
              cpdCategory,
              industryCpdCategory: mappedIndustryCpdCategory.map(
                industry => ({
                  ...industry,
                  cpdCategoryId: cpdCategory.id
                })
              )
            },
            icon
          );
        } else {
          addCpdCategory({ cpdCategory, industryCpdCategory: mappedIndustryCpdCategory }, icon);
        }
      } else {
        setValidation(validation)
      }
    }
  }),
  lifecycle({
    componentDidUpdate() {
      const validation = this.props.validation;
      if (!isValid(validation)) {
        const nextValidation = validate(this.props.cpdCategory, this.props.mappedIndustryCpdCategory);
        if (JSON.stringify(nextValidation) !== JSON.stringify(validation)) {
          this.props.setValidation(nextValidation);
        }
      }
    }
  })
)(AdminCpdCategoryEdit);


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/cpd-category-portal/cpd-category-edit.js